import { useCallback, useMemo, useState } from 'react';

import { INITIAL_PAGE, INITIAL_PAGE_SIZE } from '../constants/table';

interface IProps<T> {
  items: T[];
  initialPage?: number;
  initialPageSize?: number;
}

interface IUsePaginationResult<T> {
  page: number;
  pageSize: number;
  currentItems: T[];
  totalCount: number;
  handlePageChange: (newPage: number) => void;
  handleRowsPerPageChange: (newRowsPerPage: number) => void;
}

const usePagination = <T>({
  items,
  initialPage = INITIAL_PAGE,
  initialPageSize = INITIAL_PAGE_SIZE,
}: IProps<T>): IUsePaginationResult<T> => {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setPageSize(newRowsPerPage);
    setPage(1);
  }, []);

  const currentItems = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return items.slice(startIndex, endIndex);
  }, [items, page, pageSize]);

  return {
    page,
    pageSize,
    currentItems,
    totalCount: items.length,
    handlePageChange,
    handleRowsPerPageChange,
  };
};

export default usePagination;
